import React, { useContext, useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Eye, Plus, Filter, X, Calendar, Building2, CreditCard, RefreshCw, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import subscriptionServices from '../../../services/subscriptionServices';
import Pagination from '../../../components/Pagination';

export default function OrganizationSubscriptions() {
  const navigate = useNavigate();
  const { user, authTokens } = useContext(AuthContext);

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    searchTerm: '',
    status: '',
    sortBy: 'newest'
  });

  // Fetch subscriptions on mount and when pagination changes
  useEffect(() => {
    fetchSubscriptions();
  }, [pagination.page, pagination.page_size]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      if (filters.searchTerm) {
        params.search = filters.searchTerm;
      }

      if (filters.status) {
        params.status = filters.status;
      }

      if (filters.sortBy) {
        params.ordering = filters.sortBy;
      }

      const response = await subscriptionServices.getAllSubscriptions(
        authTokens?.access,
        params
      );

      if (response.success) {
        setSubscriptions(response.data.results || []);
        setPagination(prev => ({
          ...prev,
          count: response.data.count,
          total_pages: response.data.total_pages
        }));
      } else {
        throw new Error(response.error || 'Erreur lors du chargement des abonnements');
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors du chargement des abonnements');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSubscriptions();
  };

  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      status: '',
      sortBy: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchSubscriptions(), 100);
  };

  const handleViewDetails = (subscriptionId, organizationId) => {
    navigate(`/admin-panel/subscriptions/${subscriptionId}`);
  };

  const handleUpdateSubscription = (subscriptionId, organizationId) => {
    navigate(`/admin-panel/subscriptions/${subscriptionId}/update`);
  };

  const handleViewHistory = (organizationId) => {
    navigate(`/admin-panel/organizations/${organizationId}/subscription-history`);
  };

  const handleAddSubscription = () => {
    navigate('/admin-panel/subscriptions/add');
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Actif' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Expiré' },
      suspended: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Suspendu' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Annulé' }
    };

    const config = statusConfig[status] || statusConfig.cancelled;
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Abonnements des organisations</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez tous les abonnements du système</p>
        </div>

        <div className='flex flex-wrap gap-2'>
          <button
            onClick={handleShowFilters}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filtres</span>
          </button>

          <button
            onClick={handleAddSubscription}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Nouvel abonnement</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Erreur</h3>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className='flex items-center justify-between'>
            <div className="flex items-center mb-4">
              <Filter className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Filtres</h3>
            </div>
            <button onClick={handleShowFilters} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {/* Search filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom d'organisation..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                  className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="expired">Expiré</option>
                <option value="suspended">Suspendu</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>

            {/* Sort by filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Trier par
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="expiring_soon">Expire bientôt</option>
              </select>
            </div>
          </div>

          {/* Filter action buttons */}
          <div className="flex items-center space-x-3 justify-end">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {/* Subscriptions Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Organisation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date de début</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date de fin</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Jours restants</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                      <span className="ml-2 text-sm text-gray-600">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : subscriptions.length > 0 ? (
                subscriptions.map((subscription) => (
                  <tr
                    key={subscription.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-xs">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {subscription.organization?.name || 'Organisation inconnue'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {getStatusBadge(subscription.status)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {subscription.start_date
                        ? new Date(subscription.start_date).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {subscription.end_date
                        ? new Date(subscription.end_date).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {subscription.status === 'active' && subscription.days_remaining !== null ? (
                        <span className={`font-medium ${
                          subscription.days_remaining <= 7 
                            ? 'text-red-600' 
                            : subscription.days_remaining <= 30 
                            ? 'text-yellow-600' 
                            : 'text-green-600'
                        }`}>
                          {subscription.days_remaining} jours
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        {/* View Details */}
                        <button
                          onClick={() => handleViewDetails(subscription.id, subscription.organization?.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {/* Update Subscription */}
                        <button
                          onClick={() => handleUpdateSubscription(subscription.id, subscription.organization?.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Modifier l'abonnement"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>

                        {/* View History */}
                        <button
                          onClick={() => handleViewHistory(subscription.organization?.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title="Historique des abonnements"
                        >
                          <History className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <CreditCard className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-xs text-gray-500">Aucun abonnement trouvé</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="abonnements"
        />
      </div>
    </div>
  );
}
